/**
 * Labstep
 *
 * @module screens/Log/Index
 * @desc Log index
 */

import { EntityEmptyState } from 'labstep-web/components/Entity/EmptyState';
import { EntitySearchContainer as EntitySearchCursor } from 'labstep-web/components/Entity/Search/Cursor';
import { LogList } from 'labstep-web/components/Log/List';
import { Flex } from 'labstep-web/core/Flex';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { Log } from 'labstep-web/models/log.model';
import type React from 'react';
import { useMemo } from 'react';
import { filters } from './constants';
import type { LogIndexProps } from './types';

export const LogIndex: React.FC<LogIndexProps> = ({
  entityName,
  count,
  objectId,
  objectClass,
  extraParams,
  entityId,
  selectedEntitySnapshot,
  setSelectedEntitySnapshot,
  showGroup,
  hideFilters = true,
}) => {
  const params = useMemo(
    () =>
      entityName
        ? {
            [`${entityName}_id`]: entityId,
            count,
            has_request_trace: true,
            skip_total: 1,
            ...extraParams,
          }
        : {
            object_id: objectId,
            object_class: objectClass,
            count,
            has_request_trace: true,
            skip_total: 1,
            ...extraParams,
          },
    [entityName, entityId, count, objectId, objectClass, extraParams],
  );

  if (hideFilters) {
    return (
      <ReadOnMountHOC
        type="cursor"
        pagination={{
          infinite: false,
        }}
        entityName={Log.entityName}
        params={params}
        children={({ entities }) => (
          <LogList
            logs={entities}
            selectedEntitySnapshot={selectedEntitySnapshot}
            setSelectedEntitySnapshot={setSelectedEntitySnapshot}
            entityName={entityName}
            showGroup={showGroup}
          />
        )}
      />
    );
  }

  return (
    <Flex column hAlignContent="left" style={{ paddingLeft: '20px' }}>
      <EntitySearchCursor
        params={params}
        filters={filters}
        entityName={Log.entityName}
        hideFilters={hideFilters}
        noResultsMessage={
          <EntityEmptyState
            entityName={Log.entityName}
            searchParams={params}
          />
        }
      >
        {({ entities, ...rest }: any): React.ReactElement => (
          <LogList
            logs={entities}
            {...rest}
            selectedEntitySnapshot={selectedEntitySnapshot}
            setSelectedEntitySnapshot={setSelectedEntitySnapshot}
            entityName={entityName}
            showGroup={showGroup}
          />
        )}
      </EntitySearchCursor>
    </Flex>
  );
};
